'use client';

/**
 * SlideOver — canonical right slide-over chrome for the portal.
 *
 * Every right slide-over in the app should build on this component so the
 * chrome stays consistent:
 *
 *   Desktop: fixed right panel sliding in over a blurred backdrop
 *            (CapacitorModalHeader accent-bar header).
 *   Mobile:  full-screen surface using CapacitorModalFilterHeader
 *            (centered icon + optional ModalFilterBar row).
 *
 * Widths:
 *   'default' → max-w-2xl — config / picker flows      → <ConfigSlideOver>
 *   'medium'  → max-w-3xl — dense forms (e.g. PolicySlideOver)
 *   'wide'    → max-w-5xl — detail viewers with tabs   → <WideSlideOver>
 *
 * Owned behaviour (callers must NOT re-implement):
 *   - backdrop (zIndex) + panel (zIndex + 1) with animate-slide-in-right
 *   - body scroll lock (refcounted for stacked slide-overs)
 *   - Escape → onClose for the TOPMOST slide-over only
 *   - mobile safe-area spacer + shared-header visibility tracking
 *   - header (icon/title/subtitle/badges/actions/close X)
 *   - optional toolbar band, tab bar, loading state, footer band
 *
 * Usage — config picker:
 *   <ConfigSlideOver onClose={close} icon={Shield} title="Add Access"
 *     subtitle={...} toolbar={<SearchRow/>} footer={
 *       <SlideOverFooter summary={`${n} selected`}>
 *         <SlideOverButton variant="primary" onClick={save}>Save</SlideOverButton>
 *         <SlideOverButton variant="outline" onClick={close}>Cancel</SlideOverButton>
 *       </SlideOverFooter>
 *     }>
 *     {body}
 *   </ConfigSlideOver>
 *
 * Usage — detail viewer:
 *   <WideSlideOver onClose={close} customIcon={avatar} title={name}
 *     tabs={tabs} activeTab={tab} onTabChange={setTab}
 *     loading={loading} hasContent={!!data} footer={...}>
 *     {tab === 'x' && <XTab/>}
 *   </WideSlideOver>
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useResponsive } from '@/hooks/useResponsive';
import { isCapacitor } from '@/lib/platformUtils';
import CapacitorModalHeader from '@/components/CapacitorModalHeader';
import CapacitorModalFilterHeader, {
  capacitorHeaderActionClass,
} from '@/components/CapacitorModalFilterHeader';
import { MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// Types
// ============================================================================

export type SlideOverSize = 'default' | 'medium' | 'wide';

const SIZE_CLASS: Record<SlideOverSize, string> = {
  default: 'max-w-2xl',
  medium: 'max-w-3xl',
  wide: 'max-w-5xl',
};

export interface SlideOverTabItem {
  id: string;
  label: string;
  count?: number;
}

export interface SlideOverProps {
  onClose: () => void;
  /** Panel width on desktop — 'default' (2xl) | 'medium' (3xl) | 'wide' (5xl). */
  size?: SlideOverSize;

  // ---- Header ----
  /** Lucide icon (desktop pill + mobile icon circle). */
  icon?: LucideIcon;
  /** Pre-rendered icon node (avatar etc.) — takes priority over `icon`. */
  customIcon?: ReactNode;
  title: string;
  /** Inline badge(s) rendered beside the title. */
  titleExtra?: ReactNode;
  subtitle?: ReactNode;
  /** Accent colour — defaults to client branding primary, then action blue. */
  accentColor?: string;
  /** Extra buttons rendered before the close X (both headers). */
  headerActions?: ReactNode;
  showCloseButton?: boolean;
  /** Show the realtime connection dot in the desktop header. Default false —
      connection status is not meaningful inside a slide-over. */
  showRealtimeStatus?: boolean;
  /** Buttons pinned top-left on the MOBILE header (FilterHeader leadingActions). */
  mobileLeadingActions?: ReactNode;
  /** Filter row inside the MOBILE header — pass a <ModalFilterBar/>. */
  mobileFilterBar?: ReactNode;

  /** Fixed band between the header and tabs/content (selectors, search rows). */
  toolbar?: ReactNode;

  // ---- Tabs ----
  tabs?: SlideOverTabItem[];
  activeTab?: string;
  onTabChange?: (id: string) => void;

  // ---- Content ----
  /** Full-height branded spinner replaces the body while true. */
  loading?: boolean;
  loadingMessage?: string;
  /** Gates children + footer — false shows `emptyState` (or nothing). */
  hasContent?: boolean;
  /** Rendered in the body when loaded but `hasContent` is false. */
  emptyState?: ReactNode;
  /** Extra classes for the scroll container (replaces default padding). */
  contentClassName?: string;
  children?: ReactNode;

  /** Footer band content — use <SlideOverFooter> for the standard layout. */
  footer?: ReactNode;

  // ---- Stacking & dismissal ----
  /** Backdrop z-index; the panel renders at zIndex + 1. Raise for stacked
      slide-overs (e.g. 94 puts the panel at z-95 above a z-91 parent). */
  zIndex?: number;
  closeOnBackdropClick?: boolean;
  /** Escape closes only the topmost mounted slide-over. Default true. */
  closeOnEscape?: boolean;

  // ---- Tour hooks ----
  panelTourId?: string;
  headerTourId?: string;
  tabsTourId?: string;
  contentTourId?: string;

  /** Ref attached to the scrollable content div (scroll-restore patterns). */
  scrollRef?: MutableRefObject<HTMLDivElement | null>;
  className?: string;
}

// ============================================================================
// Internals — slide-over stack (Escape only closes the topmost) + scroll lock
// ============================================================================

const openSlideOvers: symbol[] = [];
let scrollLockCount = 0;

function lockBodyScroll() {
  scrollLockCount++;
  document.body.style.overflow = 'hidden';
}

function unlockBodyScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) document.body.style.overflow = '';
}

// ============================================================================
// Context — shared by the layout helpers below
// ============================================================================

interface SlideOverChrome {
  isMobilePanel: boolean;
  isDarkMode: boolean;
  accentColor: string;
  /** Fixed ACTION_BLUE — action buttons; never overridden by client branding. */
  actionColor: string;
}

const SlideOverContext = createContext<SlideOverChrome | null>(null);

/** Chrome state for SlideOver helpers. Works standalone too (theme fallback). */
export function useSlideOverChrome(): SlideOverChrome {
  const ctx = useContext(SlideOverContext);
  const { currentTheme, actionColor, clientBranding } = useTheme();
  const { isMobile } = useResponsive();
  if (ctx) return ctx;
  return {
    isMobilePanel: isCapacitor() || isMobile,
    isDarkMode: currentTheme?.mode === 'dark',
    accentColor: clientBranding?.primary_color || actionColor,
    actionColor,
  };
}

// ============================================================================
// SlideOver
// ============================================================================

export default function SlideOver({
  onClose,
  size = 'default',
  icon,
  customIcon,
  title,
  titleExtra,
  subtitle,
  accentColor,
  headerActions,
  showCloseButton = true,
  showRealtimeStatus = false,
  mobileLeadingActions,
  mobileFilterBar,
  toolbar,
  tabs,
  activeTab,
  onTabChange,
  loading = false,
  loadingMessage = 'Loading',
  hasContent = true,
  emptyState,
  contentClassName,
  children,
  footer,
  zIndex = 90,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  panelTourId,
  headerTourId,
  tabsTourId,
  contentTourId,
  scrollRef,
  className,
}: SlideOverProps) {
  const { currentTheme, actionColor, clientBranding } = useTheme();
  const { isMobile: isMobileViewport } = useResponsive();
  const isDarkMode = currentTheme?.mode === 'dark';
  const isMobilePanel = isCapacitor() || isMobileViewport;
  const accent = accentColor || clientBranding?.primary_color || actionColor;
  const [headerHidden, setHeaderHidden] = useState(false);

  // ---- Stack registration (Escape ordering) ----
  const idRef = useRef<symbol>(Symbol('slide-over'));
  useEffect(() => {
    const id = idRef.current;
    openSlideOvers.push(id);
    return () => {
      const i = openSlideOvers.indexOf(id);
      if (i >= 0) openSlideOvers.splice(i, 1);
    };
  }, []);

  useEffect(() => {
    if (!closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openSlideOvers[openSlideOvers.length - 1] !== idRef.current) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeOnEscape, onClose]);

  // ---- Body scroll lock (refcounted for stacked slide-overs) ----
  useEffect(() => {
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, []);

  // ---- Shared-header visibility on mobile (same pattern as ItemSearchModal) ----
  useEffect(() => {
    if (!isMobilePanel) {
      // Defer into a microtask — a synchronous setState in the effect body
      // triggers cascading renders (react-hooks/set-state-in-effect).
      queueMicrotask(() => setHeaderHidden(false));
      return;
    }
    const handleHeaderChange = (event: Event) => {
      const visible = (event as CustomEvent).detail?.visible;
      setHeaderHidden(!visible);
    };
    window.addEventListener('headerVisibilityChange', handleHeaderChange);
    return () => window.removeEventListener('headerVisibilityChange', handleHeaderChange);
  }, [isMobilePanel]);

  useEffect(() => {
    if (isMobilePanel) {
      window.dispatchEvent(new Event('showHeader'));
    }
  }, [isMobilePanel]);

  const chrome: SlideOverChrome = { isMobilePanel, isDarkMode, accentColor: accent, actionColor };
  const showBody = !loading && hasContent;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300"
        style={{ zIndex }}
        onClick={closeOnBackdropClick ? onClose : undefined}
      />

      {/* Panel */}
      <div
        data-tour-id={panelTourId}
        role="dialog"
        aria-modal="true"
        className={cn(
          'fixed right-0 flex w-full animate-slide-in-right flex-col shadow-2xl',
          isDarkMode ? 'bg-gray-900' : 'bg-white',
          isMobilePanel ? 'max-w-full' : SIZE_CLASS[size],
          className,
        )}
        style={{
          zIndex: zIndex + 1,
          ...(isMobilePanel
            ? {
                top: '0px',
                bottom: 'var(--safe-area-inset-bottom)',
                transition: 'top 300ms ease',
              }
            : {
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
                top: 'var(--safe-area-inset-top)',
                bottom: 'var(--safe-area-inset-bottom)',
                height:
                  'calc(100% - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))',
              }),
        }}
      >
        <SlideOverContext.Provider value={chrome}>
          {/* Mobile top safe-area spacer */}
          {isMobilePanel && (
            <div
              aria-hidden="true"
              className={cn('flex-shrink-0', isDarkMode ? 'bg-gray-800' : 'bg-white')}
              style={{ height: headerHidden ? 0 : MOBILE_SAFE_AREA_TOP }}
            />
          )}

          {/* Header — FilterHeader (centered icon) on mobile, accent-bar header on desktop */}
          {isMobilePanel ? (
            <CapacitorModalFilterHeader
              icon={icon}
              customIcon={customIcon}
              title={title}
              titleExtra={titleExtra}
              iconColor={accent}
              subtitle={subtitle}
              isDark={isDarkMode}
              onClose={onClose}
              leadingActions={mobileLeadingActions}
              actions={headerActions}
              showCloseButton={showCloseButton}
              filterBar={mobileFilterBar}
            />
          ) : (
            <CapacitorModalHeader
              icon={icon}
              customIcon={customIcon}
              title={title}
              titleExtra={titleExtra}
              accentColor={accent}
              gradientAccent
              isDark={isDarkMode}
              subtitle={subtitle}
              showRealtimeStatus={showRealtimeStatus}
              actions={
                <>
                  {headerActions}
                  {showCloseButton && (
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Close"
                      aria-label="Close"
                    >
                      <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  )}
                </>
              }
              tourId={headerTourId}
            />
          )}

          {/* Toolbar band (selectors / search) */}
          {toolbar && (
            <div
              className={cn(
                'flex-shrink-0 border-b border-gray-200 dark:border-gray-800',
                isMobilePanel ? 'px-3 py-3' : 'px-6 py-3',
              )}
            >
              {toolbar}
            </div>
          )}

          {/* Tab bar — horizontally scrollable so wide tab sets survive mobile */}
          {tabs && tabs.length > 0 && (
            <div
              data-tour-id={tabsTourId}
              className={cn(
                'flex flex-shrink-0 gap-1 overflow-x-auto border-b border-gray-200 no-scrollbar dark:border-gray-800',
                isMobilePanel ? 'px-3' : 'px-6',
              )}
              style={{
                background: `linear-gradient(180deg, ${accent}08 0%, transparent 100%)`,
              }}
            >
              {tabs.map((t) => (
                <SlideOverTabButton
                  key={t.id}
                  active={activeTab === t.id}
                  onClick={() => onTabChange?.(t.id)}
                  label={t.label}
                  count={t.count}
                  accentColor={accent}
                />
              ))}
            </div>
          )}

          {/* Scrollable content — `contentClassName` replaces the whole
              container so callers can own their own fixed bands + inner
              scroll regions (e.g. filter section + results list). */}
          <div
            data-tour-id={contentTourId}
            ref={scrollRef}
            className={contentClassName ?? cn(
              'flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950',
              isMobilePanel ? 'px-3 py-3' : 'px-6 py-4',
            )}
          >
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <BrandedLoadingSpinner
                  mode="compact"
                  size="sm"
                  message={loadingMessage}
                  showTagline={false}
                  showProgressBar={false}
                />
              </div>
            ) : showBody ? (
              children
            ) : (
              emptyState ?? null
            )}
          </div>

          {/* Footer band */}
          {footer && showBody && (
            <div
              className={cn(
                'flex-shrink-0 border-t',
                isDarkMode
                  ? 'border-gray-700 bg-gray-800'
                  : 'border-gray-200 bg-gray-50',
                isMobilePanel ? 'px-3 py-3' : 'px-6 py-3',
              )}
              style={
                isMobilePanel
                  ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }
                  : undefined
              }
            >
              {footer}
            </div>
          )}
        </SlideOverContext.Provider>
      </div>
    </>
  );
}

// ============================================================================
// Width presets — the two canonical entry points plus the base component
// ============================================================================

/** Wide detail slide-over (max-w-5xl) — tabbed detail viewers. */
export function WideSlideOver(props: Omit<SlideOverProps, 'size'>) {
  return <SlideOver {...props} size="wide" />;
}

/** Normal-width config slide-over (max-w-2xl) — pickers, grants, small forms. */
export function ConfigSlideOver(props: Omit<SlideOverProps, 'size'>) {
  return <SlideOver {...props} size="default" />;
}

// ============================================================================
// Helpers — keep footer/button/tab styling identical across every slide-over
// ============================================================================

/** Canonical tab button — accent-tinted label + underline when active. */
export function SlideOverTabButton({
  active,
  onClick,
  label,
  count,
  accentColor,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  accentColor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex-shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors',
        active
          ? accentColor
            ? 'rounded-t-md'
            : 'text-blue-600 dark:text-blue-400'
          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/60 dark:hover:text-gray-300',
      )}
      style={
        active && accentColor
          ? { color: accentColor, backgroundColor: `${accentColor}14` }
          : undefined
      }
    >
      {label}
      {count !== undefined && count > 0 && (
        <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          {count}
        </span>
      )}
      {active && (
        <span
          className={cn(
            'absolute bottom-0 left-0 right-0 h-0.5',
            accentColor ? 'rounded-full' : 'bg-blue-600 dark:bg-blue-400',
          )}
          style={accentColor ? { backgroundColor: accentColor } : undefined}
        />
      )}
    </button>
  );
}

/**
 * Standard footer layout: optional summary text on the left, actions on the
 * right (stacked full-width on mobile). Pass <SlideOverButton> children.
 */
export function SlideOverFooter({
  summary,
  children,
  className,
}: {
  /** Left-aligned status text (e.g. "3 selected" / "5 policies set"). */
  summary?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const { isMobilePanel, isDarkMode } = useSlideOverChrome();
  return (
    <div
      className={cn(
        isMobilePanel
          ? ''
          : `flex items-center gap-3 ${summary != null ? 'justify-between' : 'justify-end'}`,
        className,
      )}
    >
      {summary != null && (
        <div
          className={cn(
            'text-sm',
            isDarkMode ? 'text-gray-400' : 'text-gray-500',
            isMobilePanel && 'mb-3',
          )}
        >
          {summary}
        </div>
      )}
      <div
        className={cn(
          'flex',
          isMobilePanel ? 'w-full flex-col gap-2' : 'items-center justify-end gap-2',
        )}
      >
        {children}
      </div>
    </div>
  );
}

export interface SlideOverButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 'primary' = action blue fill, 'secondary' = grey fill, 'outline' = bordered. */
  variant?: 'primary' | 'secondary' | 'outline';
  icon?: LucideIcon;
  /** Renders a spinning icon instead (e.g. while saving). */
  spinning?: boolean;
}

/** Standard footer action button — full-width on mobile, action blue primary. */
export function SlideOverButton({
  variant = 'secondary',
  icon: Icon,
  spinning,
  className,
  children,
  style,
  ...rest
}: SlideOverButtonProps) {
  const { isMobilePanel, isDarkMode, actionColor } = useSlideOverChrome();
  return (
    <button
      type="button"
      className={cn(
        'rounded-lg text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        isMobilePanel ? 'w-full py-3 font-medium' : 'px-4 py-2',
        variant === 'primary' && 'text-white',
        variant === 'secondary' &&
          (isDarkMode
            ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'),
        variant === 'outline' &&
          (isDarkMode
            ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
            : 'border border-gray-200 text-gray-600 hover:bg-gray-200'),
        className,
      )}
      style={
        variant === 'primary' && !rest.disabled
          ? { backgroundColor: actionColor, ...style }
          : variant === 'primary'
            ? { backgroundColor: `${actionColor}80`, ...style }
            : style
      }
      {...rest}
    >
      <span className="inline-flex items-center justify-center gap-2">
        {spinning ? (
          <svg
            className="h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        ) : (
          Icon && <Icon className="h-4 w-4" />
        )}
        {children}
      </span>
    </button>
  );
}

/**
 * Icon button for the slide-over header — bordered square on mobile
 * (capacitorHeaderActionClass), ghost hover style on desktop.
 */
export function SlideOverHeaderAction({
  icon: Icon,
  title,
  onClick,
  disabled,
  dataTourId,
}: {
  icon: LucideIcon;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  dataTourId?: string;
}) {
  const { isMobilePanel, isDarkMode } = useSlideOverChrome();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-tour-id={dataTourId}
      className={
        isMobilePanel
          ? capacitorHeaderActionClass(isDarkMode)
          : 'rounded-lg p-1.5 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700'
      }
    >
      <Icon
        className={cn(
          'h-4 w-4',
          isMobilePanel ? undefined : 'text-gray-500 dark:text-gray-400',
        )}
      />
    </button>
  );
}
